// An X-ray's controls in the toolbar, after the annotation tools: the window
// and level tool, which drags the window on the image as any viewer does (W;
// WindowDrag), and Invert (I), each its own button (user, 2026-10-16). The
// window's numbers are in the box top right (WindowBox).

import type { ReactNode } from 'react';
import { SunMoon } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

function Toggle({
  label,
  tip = label,
  pressed,
  onClick,
  children,
}: {
  label: string;
  /** What the tooltip says, when it says more than the button's name. */
  tip?: string;
  pressed: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          aria-pressed={pressed}
          onClick={onClick}
          className={cn(
            'inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors',
            pressed ? 'bg-accent text-foreground' : 'text-muted-foreground hover:bg-accent hover:text-foreground',
          )}
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top">{tip}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Invert's icon: the window box's black-to-white ramp in a square (user,
 * 2026-10-16), drawn as the icon set draws (24 px grid, 2 px stroke).
 */
function RampIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 21 21 3v16a2 2 0 0 1-2 2z" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function WindowControl({
  armed,
  onArm,
  inverted,
  onInvert,
}: {
  armed: boolean;
  onArm: () => void;
  inverted: boolean;
  onInvert: () => void;
}) {
  return (
    <div className="flex items-center gap-0.5">
      <Toggle label="Window and level (W)" tip="Adjust Window and Level by clicking on the image (W)" pressed={armed} onClick={onArm}>
        <SunMoon className="h-4 w-4" />
      </Toggle>
      <Toggle label="Invert (I)" pressed={inverted} onClick={onInvert}>
        <RampIcon className="h-4 w-4" />
      </Toggle>
    </div>
  );
}
